"use client"

import { useEffect, useState } from "react"
import { useTranslations } from "next-intl"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import Link from "next/link"
import { isSkipAdsGrantActive } from "@/lib/skip-ads"

const STORAGE_KEY = "goonru_consent_v2"

interface ConsentPrefs {
  analytics: boolean
  personalizedAds: boolean
}

function readStoredConsent(): ConsentPrefs | null {
  if (typeof window === "undefined") return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (typeof parsed.analytics === "boolean" && typeof parsed.personalizedAds === "boolean") return parsed
    return null
  } catch {
    return null
  }
}

async function logConsent(choice: "accepted" | "declined" | "customized", prefs: ConsentPrefs) {
  try {
    await fetch("/api/cookie-consent/log", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        consent_choice: choice,
        analytics_consent: prefs.analytics,
        personalized_ads_consent: prefs.personalizedAds,
      }),
    })
  } catch (error) {
    console.error("Failed to log consent:", error)
  }
}

function commit(prefs: ConsentPrefs, choice: "accepted" | "declined" | "customized") {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
  window.dispatchEvent(new Event("cookie-consent-changed"))
  window.dispatchEvent(new Event("storage"))
  void logConsent(choice, prefs)
}

export function CookieConsent() {
  const t = useTranslations("cookieConsent")
  const [consent, setConsent] = useState<ConsentPrefs | "loading" | null>("loading")
  const [customizing, setCustomizing] = useState(false)
  const [draftAnalytics, setDraftAnalytics] = useState(false)
  const [draftAds, setDraftAds] = useState(false)

  useEffect(() => {
    setConsent(readStoredConsent())
  }, [])

  const handleAcceptAll = () => {
    const prefs = { analytics: true, personalizedAds: true }
    commit(prefs, "accepted")
    setConsent(prefs)
  }

  const handleNecessaryOnly = () => {
    const prefs = { analytics: false, personalizedAds: false }
    commit(prefs, "declined")
    setConsent(prefs)
  }

  const handleSaveCustom = () => {
    const prefs = { analytics: draftAnalytics, personalizedAds: draftAds }
    commit(prefs, "customized")
    setConsent(prefs)
    setCustomizing(false)
  }

  if (consent !== null) return null

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9998] px-2 py-4 md:p-6">
      <div className="max-w-2xl mx-auto md:mx-0 md:max-w-md bg-card/95 backdrop-blur-md border border-border/50 rounded-xl shadow-2xl py-3.5 px-2.5 flex flex-col gap-3">
        {!customizing ? (
          <>
            <p className="text-xs text-center text-foreground/80 leading-snug">
              {t.rich("description", {
                link: (chunks) => (
                  <Link href="/legal/cookies" className="underline hover:text-foreground transition-colors">
                    {chunks}
                  </Link>
                ),
              })}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Button size="sm" variant="outline" onClick={handleNecessaryOnly} className="text-xs">
                {t("necessaryOnly")}
              </Button>
              <Button size="sm" onClick={handleAcceptAll} className="text-xs">
                {t("acceptAll")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm font-medium">{t("preferencesTitle")}</p>
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <Label htmlFor="consent-analytics" className="text-xs font-medium">{t("analyticsLabel")}</Label>
                <p className="text-xs text-muted-foreground">{t("analyticsDescription")}</p>
              </div>
              <Switch id="consent-analytics" checked={draftAnalytics} onCheckedChange={setDraftAnalytics} />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <Label htmlFor="consent-ads" className="text-xs font-medium">{t("personalizedAdsLabel")}</Label>
                <p className="text-xs text-muted-foreground">{t("personalizedAdsDescription")}</p>
              </div>
              <Switch id="consent-ads" checked={draftAds} onCheckedChange={setDraftAds} />
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button size="sm" variant="ghost" onClick={() => setCustomizing(false)} className="text-xs">
                {t("back")}
              </Button>
              <Button size="sm" onClick={handleSaveCustom} className="text-xs">
                {t("savePreferences")}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/** Hook to check analytics consent (page-view tracking, tag suggestion improvement). */
export function useHasAnalyticsConsent(): boolean {
  // Lazy-initialized from localStorage synchronously on first render — NOT
  // useState(false). A component that gates a one-shot mount effect on this
  // value (e.g. "if consented, fetch this ad; else close immediately") would
  // otherwise always see false on that very first render, since the effect
  // below that reads the real value hasn't run yet — a real bug found live:
  // InterstitialAd's own mount effect closed itself on every single open
  // because of exactly this race, regardless of actual consent.
  const [consented, setConsented] = useState(() => readStoredConsent()?.analytics === true)

  useEffect(() => {
    const check = () => setConsented(readStoredConsent()?.analytics === true)
    check()
    window.addEventListener("storage", check)
    window.addEventListener("cookie-consent-changed", check)
    return () => {
      window.removeEventListener("storage", check)
      window.removeEventListener("cookie-consent-changed", check)
    }
  }, [])

  return consented
}

/** Hook to check personalized-ads consent (behavioral tag-affinity targeting). */
export function useHasPersonalizedAdsConsent(): boolean {
  // Lazy-initialized synchronously — see useHasAnalyticsConsent above for why.
  const [consented, setConsented] = useState(() => readStoredConsent()?.personalizedAds === true)

  useEffect(() => {
    const check = () => setConsented(readStoredConsent()?.personalizedAds === true)
    check()
    window.addEventListener("storage", check)
    window.addEventListener("cookie-consent-changed", check)
    return () => {
      window.removeEventListener("storage", check)
      window.removeEventListener("cookie-consent-changed", check)
    }
  }, [])

  return consented
}

/** Synchronous check for non-React code (e.g. lib/analytics-client.ts). */
export function hasAnalyticsConsentSync(): boolean {
  return readStoredConsent()?.analytics === true
}

/** Synchronous check for non-React code. */
export function hasPersonalizedAdsConsentSync(): boolean {
  return readStoredConsent()?.personalizedAds === true
}

/** Utility to change consent preference programmatically (e.g. from a settings page). */
export function setConsentPreference(prefs: ConsentPrefs | null) {
  if (typeof window === "undefined") return
  if (prefs === null) {
    localStorage.removeItem(STORAGE_KEY)
    window.dispatchEvent(new Event("cookie-consent-changed"))
    window.dispatchEvent(new Event("storage"))
    return
  }
  commit(prefs, "customized")
}

/** Read current consent state (or null if the user hasn't decided yet). */
export function getConsentState(): ConsentPrefs | null {
  return readStoredConsent()
}

// Every ad-network component (components/ads/*) calls useHasConsent()/
// hasConsentSync() to decide whether to render at all. This used to alias
// useHasPersonalizedAdsConsent/hasPersonalizedAdsConsentSync, so choosing
// "Necessary Only" on the banner (or simply not having decided yet — a
// fresh guest/incognito session, before the banner's even been clicked)
// hid every ad on the site outright, not just personalization/targeting.
// Explicit product decision: a visitor sees ads regardless of that choice —
// "Necessary Only" declines analytics and behavioral ad targeting, it does
// not opt out of ads existing. So this is deliberately NOT wired to
// personalizedAds (or to consent at all): every visitor counts as
// consented for the purpose of "does an ad slot render." Real per-visitor
// personalization (if that's ever built as a distinct targeting feature)
// should read useHasPersonalizedAdsConsent directly instead of this.
// Also the enforcement point for the Skip-Ads reward (lib/skip-ads.ts) — see
// that file's own comment for why piggybacking on this existing choke point
// (rather than touching every ad component individually) is the reliable
// way to suppress every placement at once during an active grant.
export function useHasConsent(): boolean {
  const [grantActive, setGrantActive] = useState(isSkipAdsGrantActive())
  useEffect(() => {
    const check = () => setGrantActive(isSkipAdsGrantActive())
    check()
    const interval = setInterval(check, 5000)
    return () => clearInterval(interval)
  }, [])
  return !grantActive
}
export function hasConsentSync(): boolean {
  return !isSkipAdsGrantActive()
}
